import { createContext, use, useContext, useEffect, useState } from "react";
import { reusableMaxToken } from './components/Auth/maxSession';
import { loginMaxWebApp } from './api_social_auth';
import Cookies from "js-cookie";
import { useLocation, useNavigate } from "react-router";
import { Box, CssVarsProvider, Typography } from "@mui/joy";
import { APIGetChatBySlug, APIGetProfile, APIGetProfileStatus, APIVerifyToken } from "./api";
import ProfileEditScreen from "./components/Onboarding/ProfileEditScreen";
import { isProfileDone } from "./components/Onboarding/onboardingConfig";
import { theme } from "./App";

import { START_INFO_SHOWN_KEY } from "./utils";
import UserBlockPage from "./pages/UserBlockPage";
import StartInfo from "./components/StartInfo";
import useMediaQuery from '@mui/material/useMediaQuery';
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import OnboardingPage from "./components/Onboarding/OnboardingPage";
import { BrandSplash, StatusScreen, PrimaryButton, IconPlug } from "./components/Auth/ui";


// Страницы, которые открываются без входа (в том числе по ссылкам из писем).
// Всё остальное гостю показывает анкету-онбординг.
const PUBLIC_AUTH_PAGES = {
    '/login': LoginPage,
    '/forgot-password': ForgotPasswordPage,
    '/reset-password': ResetPasswordPage,
    '/verify-email': VerifyEmailPage,
};


export const LayoutContext = createContext();

// Функция для проверки и парсинга Telegram WebApp данных
const getTelegramWebAppData = () => {
  if (window.Telegram?.WebApp) {
    const tg = window.Telegram.WebApp;
    
    // Инициализируем WebApp
    tg.ready();
    tg.expand();
    
    // Получаем данные пользователя
    const initData = tg.initData || '';
    const user = tg.initDataUnsafe?.user;
    
    if (user) {
      return {
        id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        username: user.username,
        language_code: user.language_code,
        is_premium: user.is_premium || false,
        initData: initData
      };
    }
  }
  return null;
};

// Функция для проверки и парсинга MAX WebApp данных
const getMaxWebAppData = () => {
    // Только реальные SDK MAX, не любой попавшийся window.WebApp
    const maxWebApp = window.WebApp || window.Max?.WebApp || window.MaxApp || window.maxSdk;
    if (maxWebApp?.initData && maxWebApp?.initDataUnsafe?.user) {
        try {
        maxWebApp.ready?.();
        maxWebApp.expand?.();

        const user = maxWebApp.initDataUnsafe?.user || maxWebApp.user;
        if (user) {
            return {
            id: user.id,
            first_name: user.first_name || user.name,
            username: user.username,
            initData: maxWebApp.initData || '',
            };
        }
        } catch (e) {
        console.warn('MAX WebApp init error:', e);
        }
    }
    return null;
};

// Функция для создания заголовков с Telegram данными
const getTelegramAuthHeaders = (telegramData) => {
  if (!telegramData) return {};
  
  return {
    'X-Telegram-Init-Data': telegramData.initData || '',
    'X-Telegram-User-ID': telegramData.id.toString()
  };
};


export default function LayoutProvider({ children }) {
    const navigate = useNavigate();
    const location = useLocation();

    const [ deviceType, setDeviceType ] = useState('');
    const [inviteChatData, setInviteChatData] = useState(null);

    const [ token, setToken ] = useState(null);
    const [ isVerified, setIsVerified ] = useState(false);
    const [ isVerifying, setIsVerifying ] = useState(true);
    const [ currentUser, setCurrentUser ] = useState(null);
    const [ systemSettings, setSystemSettings ] = useState(null);

    // Анкета пользователя
    const [ profileUser, setProfileUser ] = useState(null);
    // Модальное окно настроек профиля
    const [ modalQuestionnaire, setModalQuestionnaire ] = useState({ open: false });
    // Статус профиля
    const [ profileStatus, setProfileStatus ] = useState({
        has_profile: false,
        basic_complete: false,     // мини-анкета пройдена
        full_complete: false,      // анкета дозаполнена — можно в ленту
        missing: [],
        require_full: true,
        partner_preferences_complete: false,
        is_accepted: false
    });
    // Подтверждение профиля
    const [ isCheckingProfile, setIsCheckingProfile ] = useState(true);

    // Модальное окно начала знакомства в проектом
    const [ modalStartInfo, setModalStartInfo ] = useState({ open: false });
    // Окно блокировки пользователя
    const [ userBlockPage, setUserBlockPage ] = useState({ block: false, cause: null });

    // Окно Жалобы
    const [ modalComplaint, setModalComplaint ] = useState({ open: false, user: null });
    // Окно Пожеланий
    const [ modalSuggestion, setModalSuggestion ] = useState({ open: false });

    // Лента анкет
    const [ updateStatusListProfiles, setUpdateStatusListProfiles ] = useState(false);

    // Окно фильтра
    const [ modalSecondQuestionnaire, setModalSecondQuestionnaire ] = useState({ open: false });

    // Окно тарифов
    const [ tariffModalOpen, setTariffModalOpen ] = useState(false);

    // Окно докупки лайков
    const [ purchaseModalOpen, setPurchaseModalOpen ] = useState(false);

    // Чаты
    const [ chats, setChats ] = useState([]);

    // Добавляем состояние для отслеживания размера экрана
    // const [ isMobile, setIsMobile ] = useState(false);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // 'sm' - стандартный breakpoint для мобильных (обычно 600px)


    const [isLoginRequired, setIsLoginRequired] = useState(false);


    useEffect(() => {
        const _tg = window?.Telegram?.WebApp;
            
        // Функция для проверки размера экрана
        const checkScreenSize = (e) => {
            const isMobileDevice = window.screen.width <= 550;
            return isMobileDevice;
        };
        
        // отключить вертикальный свайп
        _tg?.disableVerticalSwipes();

        try {
            if (_tg) {
                // Сообщаем Telegram, что WebApp готово
                _tg?.ready();
                
                // Проверяем размер экрана
                const shouldFullscreen = checkScreenSize();
                
                console.log("shouldFullscreen", shouldFullscreen);
                if (shouldFullscreen) {
                    // Разворачиваем WebApp на весь экран только для мобильных устройств
                    _tg?.expand();
                    _tg?.requestFullscreen();
                }
                
                // отключить вертикальный свайп
                _tg?.disableVerticalSwipes();
                // Настраиваем цвет верхней шапки ТГ
                _tg?.setHeaderColor('#8a4dff');
                _tg?.setBackgroundColor('#ffffffff');
            }
            
            // Добавляем обработчик изменения размера окна
            window.addEventListener('resize', checkScreenSize);
            
            // Проверяем размер при монтировании
            checkScreenSize();
        }
        catch (error) {
            console.error('Ошибка инициализации Telegram WebApp:', error);
        }
        
        // Очистка обработчика при размонтировании
        return () => {
            window.removeEventListener('resize', checkScreenSize);
        };
    }, []);

    // Определяем тип устройства
    useEffect(() => {
        const userAgent = navigator.userAgent;
        if (userAgent.match(/Android/i) || userAgent.match(/iPhone|iPad|iPod/i)) {
            setDeviceType('Phone');
        } else if (userAgent.match(/Mac|Win|Linux/i)) {
            setDeviceType('Desktop');
        } else {
            setDeviceType('Unknown');
        }
    }, []);

    // Проверяем, было ли уже показано окно
    useEffect(() => {
        const hasStartInfoBeenShown = localStorage.getItem(START_INFO_SHOWN_KEY);
        // localStorage.removeItem(START_INFO_SHOWN_KEY);

        if (!hasStartInfoBeenShown) {
            // Если еще не показывали - открываем окно
            setModalStartInfo({ open: true });
        } else {
            // Если уже показывали - закрываем окно
            setModalStartInfo({ open: false });
        }
    }, []);
    
    // Обработка параметров URL при загрузке
    useEffect(() => {
        const handleInviteParams = async () => {
            const params = new URLSearchParams(location.search);
            const chatSlug = params.get('chat');
            
            if (chatSlug && token) {
                try {
                    const chatData = await APIGetChatBySlug(token, chatSlug);
                    setInviteChatData(chatData);
                    
                    // Если пользователь уже участник - сразу открываем чат
                    if (chatData.is_member) {
                        navigate(`/chats?chatId=${chatData.chat_id}`);
                    }
                } catch (error) {
                    console.error('Ошибка загрузки чата по приглашению:', error);
                }
            }
        };
        
        if (token && isVerified) {
            handleInviteParams();
        }
    }, [token, isVerified, location]);

    // Проверка токена при монтировании
    useEffect(() => {
        const verifyToken = async () => {
            // Не лезем в верификацию dating-пользователя, если мы в кабинете партнёра.
            if (location.pathname.startsWith('/partner')) {
                setIsVerifying(false);
                setIsCheckingProfile(false);
                return;
            }
            try {
                const params = new URLSearchParams(location.search);
                // На страницах из писем ?token= — это токен сброса/подтверждения,
                // а не токен входа: иначе верификация падает и человек видит ошибку.
                const isPublicAuthPage = Boolean(PUBLIC_AUTH_PAGES[location.pathname]);
                const urlToken = isPublicAuthPage ? null : params.get('token');
                const urlPlatform = params.get('platform'); // может прийти из ссылки бота MAX

                const tgData = getTelegramWebAppData();
                const maxData = getMaxWebAppData();

                let authToken;
                let full_name;
                let username;
                let platform;

                if (tgData) {
                    console.log('Обнаружен Telegram WebApp', tgData?.id);
                    authToken = tgData?.id;
                    full_name = tgData?.first_name;
                    username = tgData?.username;
                    platform = 'tg';
                } else if (maxData) {
                    console.log('Обнаружен MAX WebApp', maxData?.id);
                    authToken = reusableMaxToken(Cookies.get('authToken'), Cookies.get('authPlatform'), maxData.id);
                    if (!authToken) {
                        const maxLogin = await loginMaxWebApp(maxData.initData);
                        authToken = maxLogin.token;
                    }
                    full_name = maxData?.first_name;
                    username = maxData?.username;
                    platform = 'max';
                } else {
                    const cookieToken = Cookies.get('authToken');
                    const cookiePlatform = Cookies.get('authPlatform');
                    authToken = urlToken || cookieToken;
                    platform = urlPlatform || cookiePlatform || 'tg';

                    if (!authToken) {
                        setIsLoginRequired(true);
                        setIsVerifying(false);
                        setIsCheckingProfile(false);
                        setModalStartInfo({ open: false });
                        return;
                    }
                }

                let response;
                try {
                    response = await APIVerifyToken(authToken, { params: { full_name, username, platform } }, platform);
                } catch (e) {
                    if (maxData && e?.response?.status === 401) {
                        const fresh = await loginMaxWebApp(maxData.initData);
                        authToken = fresh.token;
                        response = await APIVerifyToken(authToken, {}, platform);
                    } else throw e;
                }

                setToken(authToken);
                setCurrentUser({
                    ...response?.user,
                    tariff_info: response?.user?.tariff_info
                });
                setSystemSettings(response?.system_settings);
                setIsVerified(true);
                setIsLoginRequired(false);

                Cookies.set('authToken', authToken, { expires: 30 });
                Cookies.set('authPlatform', platform, { expires: 30 });

                // Сохраняем в localStorage чтобы getAuthConfig мог использовать
                localStorage.setItem('authPlatform', platform);

                if (urlToken) {
                    navigate(location.pathname, { replace: true });
                }

                if (response?.user?.id) {
                    await checkProfileStatus(authToken, response.user.id);
                }

            } catch (error) {
                console.error('Ошибка верификации:', error);
                const isWebApp = Boolean(getTelegramWebAppData() || getMaxWebAppData());
                if (error?.response?.status === 401 && !isWebApp) {
                    // Токен из cookie недействителен -> показываем анкету/вход
                    Cookies.remove('authToken');
                    setIsVerified(false);
                    setIsLoginRequired(true);
                } else {
                    setUserBlockPage(error?.response?.data?.user);
                    setIsVerified(false);
                    setIsLoginRequired(false);
                }
                setIsCheckingProfile(false);
            } finally {
                setIsVerifying(false);
            }
        };
        
        verifyToken();
    }, [location.pathname]);

    const checkProfileStatus = async (token, userId) => {
        try {
            const statusData = await APIGetProfileStatus(token, userId);
            setProfileStatus(statusData || {
                has_profile: false,
                basic_complete: false,
                full_complete: false,
                missing: [],
                require_full: true,
                partner_preferences_complete: false,
                is_accepted: false
            });
            
            // Анкеты нет или она не дозаполнена — продолжаем её
            if (!isProfileDone(statusData)) {
                setProfileUser(null);
                setModalQuestionnaire({ open: true });
            } else {
                // Загружаем данные профиля для предзаполнения
                const profileData = await APIGetProfile(token);
                if (profileData) {
                    setProfileUser(profileData);
                    setModalQuestionnaire({ open: false });
                }
            }
        } catch (error) {
            console.error('Ошибка проверки профиля:', error);
            // Если ошибка 404 (профиль не найден), открываем модальное окно
            if (error.response?.status === 404) {
                setProfileUser(null);
                setModalQuestionnaire({ open: true });
            }
        } finally {
            setIsCheckingProfile(false);
        }
    };

    const loadChats = async () => {
        try {
            const data = await APIGetChats(token);
            const chatsData = data?.chats || [];
            setChats(chatsData);
        } catch (error) {
            console.error('Ошибка загрузки чатов:', error);
        }
    };


    const values = {
        deviceType, setDeviceType,
        token, setToken, setIsVerified,
        isMobile,
        currentUser, setCurrentUser,
        systemSettings, setSystemSettings,
        profileStatus, setProfileStatus,

        modalStartInfo, setModalStartInfo,
        userBlockPage, setUserBlockPage,

        modalSuggestion, setModalSuggestion,
        modalComplaint, setModalComplaint,
        
        profileUser, setProfileUser,
        isCheckingProfile, setIsCheckingProfile,

        updateStatusListProfiles, setUpdateStatusListProfiles,

        modalQuestionnaire, setModalQuestionnaire,
        modalSecondQuestionnaire, setModalSecondQuestionnaire,

        tariffModalOpen, setTariffModalOpen,
        purchaseModalOpen, setPurchaseModalOpen,
        
        inviteChatData,
        setInviteChatData,

        chats, setChats, loadChats,
        
        checkProfileStatus,

        isLoginRequired,
        setIsLoginRequired,
    }
    
    return (
        <LayoutContext.Provider value={values}>
            <CssVarsProvider theme={theme}>
                { userBlockPage?.block ?
                    <UserBlockPage/>
                    :
                    isVerifying || isCheckingProfile ? <VerificationScreen /> : 
                    isLoginRequired ? <GuestScreen pathname={location.pathname} /> :
                    isVerified ? (
                        isProfileDone(profileStatus) ? 
                            children : 
                            <OnboardingPage mode="profile" />
                    ) : <VerificationFailedScreen />
                }
                
                <ProfileEditScreen />
                <StartInfo/>
            </CssVarsProvider>
        </LayoutContext.Provider>
    );
}



function QuestionnaireModalForced() {
    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            backgroundColor: 'white',
            flexDirection: 'column',
            gap: '20px'
        }}>
            {/* <Typography level="h4" color="danger" sx={{ textAlign: "center" }}>Заполните анкету для продолжения</Typography>
            <Typography sx={{ textAlign: "center" }}>Пожалуйста, заполните базовую информацию в анкете</Typography> */}
        </Box>
    );
}

// Что показываем гостю: страницу из письма/входа или анкету-онбординг
function GuestScreen({ pathname }) {
    const Page = PUBLIC_AUTH_PAGES[pathname];
    return Page ? <Page /> : <OnboardingPage />;
}

// Экран проверки входа — фирменная заставка вместо белого экрана
function VerificationScreen() {
    return <BrandSplash />;
}

// Экран неудачного входа
function VerificationFailedScreen() {
    return (
        <StatusScreen
            icon={<IconPlug />}
            title="Не удалось войти"
            text="Проверьте интернет и попробуйте ещё раз. Если не помогает — войдите заново."
        >
            <PrimaryButton onClick={() => { window.location.href = '/login'; }}>
                На страницу входа
            </PrimaryButton>
        </StatusScreen>
    );
}